import * as React from "react";
import { useTranslation } from "react-i18next";

type Props = {
  /** Selected rating, 0 = none. */
  value: number;
  onChange: (value: number) => void;
  /** Append the kit "N / 5" readout. */
  readout?: boolean;
};

const STAR_PATH =
  "M12 2l2.9 6.95 7.5.6-5.7 4.92 1.74 7.33L12 17.77l-6.44 3.93 1.74-7.33L1.6 9.55l7.5-.6L12 2z";

export function StarRatingInput({ value, onChange, readout }: Props) {
  const { t } = useTranslation('ui');
  const [hover, setHover] = React.useState(0);
  const shown = hover || value;

  return (
    <div
      className="inline-flex items-center gap-1.5 t-stars-gold"
      role="radiogroup"
      aria-label={t('rating')}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= shown;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            className={`p-2 md:p-0.5 leading-none transition [&:hover_svg]:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-1 ${
              filled ? "" : "text-primary/15"
            }`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onFocus={() => setHover(n)}
            onBlur={() => setHover(0)}
            onClick={() => onChange(n)}
          >
            <svg
              viewBox="0 0 24 24"
              className="h-7 w-7 transition-transform"
              aria-hidden="true"
            >
              <path
                d={STAR_PATH}
                fill={filled ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth={filled ? 0 : 1.2}
              />
            </svg>
          </button>
        );
      })}
      {readout ? (
        <span className="ml-3 text-micro font-medium uppercase tracking-link text-primary/60 [font-variant-numeric:tabular-nums]">
          {value > 0 ? `${value} / 5` : "— / 5"}
        </span>
      ) : null}
    </div>
  );
}
